import React from 'react';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';

const CartSidebar = ({ isOpen, onClose, onCheckout }) => {
  const { items, updateQuantity, removeFromCart, subtotal, clearCart } = useCart();

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-[60] transition-opacity"
          onClick={onClose}
          data-testid="cart-backdrop"
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed right-0 top-0 h-full w-full max-w-sm bg-white shadow-2xl z-[70] flex flex-col transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="cart-sidebar"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#ECE0DA]">
          <h2 className="font-playfair text-xl font-bold text-[#1F1410]">
            Your Cart
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#FDF8F3] transition-colors" data-testid="cart-close">
            <X size={20} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 scrollbar-thin">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12" data-testid="cart-empty">
              <ShoppingBag size={48} className="text-[#ECE0DA] mb-3" />
              <p className="text-[#7A6A63] font-medium">Your cart is empty</p>
              <p className="text-[#7A6A63] text-sm mt-1">Add some delicious items!</p>
            </div>
          ) : (
            items.map(item => (
              <div key={item.key} className="flex gap-3 items-start" data-testid={`cart-item-${item.key}`}>
                <img
                  src={item.image || 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=80&q=70'}
                  alt={item.productName}
                  className="w-16 h-16 object-cover rounded-lg border border-[#ECE0DA] shrink-0"
                  onError={e => { e.target.src = 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=80&q=70'; }}
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-playfair font-semibold text-[#1F1410] text-sm leading-tight line-clamp-2">
                    {item.productName}
                  </h4>
                  {item.size && <p className="text-xs text-[#7A6A63] mt-0.5">{item.size}</p>}
                  <p className="text-[#7A1125] font-semibold text-sm mt-1">₹{item.price}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <button onClick={() => removeFromCart(item.key)} className="text-[#7A6A63] hover:text-[#7A1125] transition-colors">
                    <Trash2 size={14} />
                  </button>
                  <div className="flex items-center gap-1 border border-[#ECE0DA] rounded-full">
                    <button
                      onClick={() => updateQuantity(item.key, item.quantity - 1)}
                      className="w-7 h-7 flex items-center justify-center hover:bg-[#FDF8F3] rounded-full transition-colors"
                      data-testid={`cart-decrease-${item.key}`}
                    >
                      <Minus size={12} />
                    </button>
                    <span className="text-sm font-semibold w-5 text-center">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.key, item.quantity + 1)}
                      className="w-7 h-7 flex items-center justify-center hover:bg-[#FDF8F3] rounded-full transition-colors"
                      data-testid={`cart-increase-${item.key}`}
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-[#ECE0DA] px-5 py-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-[#7A6A63] text-sm">Subtotal</span>
              <span className="font-playfair font-bold text-[#1F1410] text-lg" data-testid="cart-subtotal">₹{subtotal}</span>
            </div>
            <button
              onClick={onCheckout}
              className="w-full bg-[#7A1125] hover:bg-[#8B1A2B] text-white font-semibold py-3 rounded-full transition-colors duration-200"
              data-testid="checkout-button"
            >
              Proceed to Checkout
            </button>
            <button
              onClick={clearCart}
              className="w-full text-[#7A6A63] text-sm hover:text-[#7A1125] transition-colors"
              data-testid="clear-cart-button"
            >
              Clear Cart
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartSidebar;
